import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Toaster, toast } from 'react-hot-toast';
import {
  AlertCircle,
  Check,
  Shield,
  Save,
  Building2,
  Network,
  Edit3,
  Server,
  Lock,
  Info
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiRequest } from '../utils/api';

// ——————————————— Styled Components ———————————————

const PageWrapper = styled.div`
  background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
  min-height: 100vh;
  padding: 0;
  margin: 0;
`;

const Container = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
  background: white;
  min-height: 100vh;
`;

const MainContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
  background: white;
  position: relative;
`;

const FormContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: calc(100vh - 200px);

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled.div`
  padding: 2rem 2rem; /* reduced padding */
  background: ${props => props.primary ? 'white' : '#fafbfc'};
  border-right: ${props => props.primary ? '1px solid #e5e7eb' : 'none'};
  position: relative;

  @media (max-width: 1200px) {
    border-right: none;
    border-bottom: ${props => props.primary ? '1px solid #e5e7eb' : 'none'};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px; /* reduced height */
    background: ${props => props.primary ?
      'linear-gradient(90deg, #00419f, #1562b7, #1e90ff)' :
      'linear-gradient(90deg, #64748b, #475569, #334155)'};
  }
`;

const SectionHeader = styled.div`
  margin-bottom: 1.5rem; /* reduced margin */
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  letter-spacing: -0.01em;

  svg {
    margin-right: 1rem;
    color: ${props => props.primary ? '#00419f' : '#64748b'};
    width: 22px;
    height: 22px;
  }
`;

const SectionDescription = styled.p`
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 2rem;
  grid-template-columns: ${props => props.cols === 2 ? '1fr 1fr' : '1fr'};
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.mb || '1rem'}; /* reduced margin */
`;

const Label = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem; /* reduced margin */
  display: flex;
  align-items: center;
`;

const RequiredIndicator = styled.span`
  color: #ef4444;
  margin-left: 0.25rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem; /* reduced padding */
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  font-size: 0.85rem; /* smaller font */
  color: #1f2937;
  transition: all 0.3s ease;
  background: white;
  font-weight: 500;
  height: 2.5rem;

  &:focus {
    outline: none;
    border-color: #00419f;
    box-shadow: 0 0 0 4px rgba(0, 65, 159, 0.1);
    transform: translateY(-1px);
  }

  &:hover {
    border-color: #cbd5e1;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }

  &:read-only {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }

  ${props => props.error && `
    border-color: #ef4444;
    background-color: #fef2f2;

    &:focus {
      border-color: #ef4444;
      box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
    }
  `}
`;

const InfoCard = styled.div`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 1rem;
  padding: 1rem 1.5rem; /* reduced padding */
  margin-bottom: 1.5rem; /* reduced margin */
  display: flex;
  align-items: flex-start;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #00419f, #1562b7, #1e90ff);
  }
`;

const InfoIcon = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 1.5rem;
  flex-shrink: 0;
  box-shadow: 0 4px 6px -1px rgba(0, 65, 159, 0.3);
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  color: #00419f;
  margin-bottom: 0.5rem; /* reduced margin */
  letter-spacing: -0.01em;
`;

const InfoText = styled.p`
  color: #1562b7;
  font-size: 0.875rem; /* slightly smaller */
  line-height: 1.4;
  margin: 0;
  font-weight: 500;
`;

const ValidationSummary = styled.div`
  background: ${props => props.valid ?
    'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' :
    'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)'};
  border: 1px solid ${props => props.valid ? '#bbf7d0' : '#fecaca'};
  border-radius: 0.75rem;
  padding: 1rem; /* reduced */
  margin-bottom: 1rem; /* reduced */
  display: flex;
  align-items: center;
`;

const ValidationIcon = styled.div`
  margin-right: 1rem;
  color: ${props => props.valid ? '#22c55e' : '#ef4444'};
`;

const ValidationText = styled.span`
  font-size: 0.75rem; /* smaller */
  font-weight: 600;
  color: ${props => props.valid ? '#166534' : '#dc2626'};
`;

const ActionBar = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-top: 1px solid #e5e7eb;
  padding: 1rem 2rem; /* reduced padding */
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0, 65, 159, 0.2), transparent);
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1.25rem; /* reduced padding */
  font-size: 0.85rem; /* smaller */
  font-weight: 600;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 3rem; /* reduced height */
  position: relative;
  overflow: hidden;
  letter-spacing: -0.01em;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  ${props =>
    props.primary &&
    `
    background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
    color: white;
    border: none;
    box-shadow: 0 3px 6px -1px rgba(0, 65, 159, 0.3);

    &:hover {
      transform: translateY(-1.5px);
      box-shadow: 0 8px 12px -3px rgba(0, 65, 159, 0.4);
    }

    &:active {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }
  `}

  ${props =>
    props.secondary &&
    `
    background: white;
    color: #64748b;
    border: 2px solid #e5e7eb;

    &:hover {
      background: #f8fafc;
      border-color: #cbd5e1;
      transform: translateY(-1px);
      box-shadow: 0 3px 6px -1px rgba(0, 0, 0, 0.1);
    }

    &:active {
      transform: translateY(0);
    }
  `}

  svg {
    margin-right: 0.5rem; /* reduced */
  }
`;

// ——————————————— Component Logic ———————————————

function EditAssetsNetwork() {
  const navigate = useNavigate();
  const location = useLocation();
  const { asset } = location.state || {};

  const [assetData, setAssetData] = useState({
    oldName: asset?.name || '',
    newName: asset?.name || '',
    ip: Array.isArray(asset?.ips) && asset.ips.length > 0 ? asset.ips[0] : '',
  });

  const [validation, setValidation] = useState({
    step1Valid: false
  });

  // IP validation function
  const isValidIP = (ip) => {
    if (!ip) return false;

    // Basic IP/CIDR validation
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(?:\/(?:[0-9]|[1-2][0-9]|3[0-2]))?$/;
    return ipRegex.test(ip.trim());
  };

  // Validate forms
  useEffect(() => {
    const step1Valid =
      assetData.newName.trim() !== '' &&
      assetData.ip.trim() !== '' &&
      isValidIP(assetData.ip);

    setValidation({
      step1Valid
    });
  }, [assetData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAssetData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateAsset = () => {
    if (!validation.step1Valid) {
      toast.error('Please fill all required fields correctly');
      return;
    }

    const payload = {
      oldName: assetData.oldName.trim(),
      newName: assetData.newName.trim(),
      assetType: 'network',
      ips: [assetData.ip.trim()],
    };

    apiRequest('/api/assets/edit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async res => {
        const data = await res.json();
        if (res.ok) {
          toast.success(data.message || 'Network asset updated successfully!');
          navigate('/add-assets');
        } else {
          throw new Error(data.message || 'Failed to update network asset');
        }
      })
      .catch(error => {
        console.error('Error updating network asset:', error);
        toast.error(error.message || 'An error occurred while updating the asset');
      });
  };

  return (
    <PageWrapper>
      <Container>
        {/* <Header>
          <HeaderContent>
            <Title>Edit Network Asset Configuration</Title>
            <Subtitle>
              Update your network infrastructure settings with enterprise-grade
              monitoring capabilities. Modify professional network security assessment parameters.
            </Subtitle>
            <FeatureHighlight>
              <FeatureItem>
                <Shield size={20} />
                Network Security Monitoring
              </FeatureItem>
              <FeatureItem>
                <Network size={20} />
                Infrastructure Management
              </FeatureItem>
              <FeatureItem>
                <Building2 size={20} />
                Enterprise Infrastructure
              </FeatureItem>
            </FeatureHighlight>
          </HeaderContent>
        </Header> */}

        <MainContent>
          <FormContainer>
            {/* Network Configuration Section */}
            <FormSection primary>
              <SectionHeader>
                <SectionTitle primary>
                  <Edit3 size={28} />
                  Update Network Configuration
                </SectionTitle>
                <SectionDescription>
                  Update network details, including IPs and CIDRs, for thorough security monitoring and automated vulnerability
                  management across your network perimeter.
                </SectionDescription>
              </SectionHeader>

              <InfoCard>
                <InfoIcon>
                  <Shield size={24} />
                </InfoIcon>
                <InfoContent>
                  <InfoTitle>Advanced Network Security Updates</InfoTitle>
                  <InfoText>
                    Update your network asset configuration with enterprise-grade
                    security controls. All network modifications are monitored continuously and
                    comply with industry standards for infrastructure security.
                  </InfoText>
                </InfoContent>
              </InfoCard>

              <ValidationSummary valid={validation.step1Valid}>
                <ValidationIcon valid={validation.step1Valid}>
                  {validation.step1Valid ? <Check size={20} /> : <AlertCircle size={20} />}
                </ValidationIcon>
                <ValidationText valid={validation.step1Valid}>
                  {validation.step1Valid
                    ? 'All required network configuration fields are completed'
                    : 'Please complete all required fields with valid IP/CIDR notation'}
                </ValidationText>
              </ValidationSummary>

              <FormGrid cols={2}>
                <FormGroup>
                  <Label>
                    Current Asset Name
                  </Label>
                  <Input
                    type="text"
                    name="oldName"
                    value={assetData.oldName}
                    readOnly
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>
                    New Asset Name <RequiredIndicator>*</RequiredIndicator>
                  </Label>
                  <Input
                    type="text"
                    name="newName"
                    value={assetData.newName}
                    onChange={handleInputChange}
                    placeholder="Internal LAN Segment, DMZ Network, Production Subnet"
                  />
                </FormGroup>
              </FormGrid>

              <FormGroup>
                <Label>
                  Asset Type
                </Label>
                <Input
                  type="text"
                  name="type"
                  value="Network Infrastructure"
                  readOnly
                  style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </FormGroup>

              <FormGroup mb="0">
                <Label>
                  IP Address or CIDR Range <RequiredIndicator>*</RequiredIndicator>
                  {assetData.ip && !isValidIP(assetData.ip) && (
                    <span style={{ color: '#ef4444', fontSize: '0.875rem', marginLeft: '0.5rem', fontWeight: '500' }}>
                      Please enter a valid IP address or CIDR notation
                    </span>
                  )}
                </Label>
                <Input
                  type="text"
                  name="ip"
                  value={assetData.ip}
                  onChange={handleInputChange}
                  placeholder="192.168.1.0/24, 10.0.0.5, 172.16.0.0/16"
                  error={assetData.ip && !isValidIP(assetData.ip)}
                />
              </FormGroup>
            </FormSection>

            {/* Network Details Section */}
            <FormSection>
              <SectionHeader>
                <SectionTitle>
                  <Server size={28} />
                  Asset Update Details
                </SectionTitle>
                <SectionDescription>
                  Review current asset information and configuration details for enhanced
                  asset management and security monitoring capabilities during the update process.
                </SectionDescription>
              </SectionHeader>

              <InfoCard>
                <InfoIcon>
                  <Lock size={24} />
                </InfoIcon>
                <InfoContent>
                  <InfoTitle>Secure Asset Management</InfoTitle>
                  <InfoText>
                    Asset update information is protected with military-grade encryption and
                    stored in compliance with enterprise security standards. All asset modifications
                    are monitored and audited for regulatory compliance.
                  </InfoText>
                </InfoContent>
              </InfoCard>

              <FormGroup>
                <Label>
                  Original Asset Name
                  <Info size={16} style={{ marginLeft: '0.5rem', color: '#6b7280' }} />
                </Label>
                <Input
                  type="text"
                  value={assetData.oldName}
                  readOnly
                  style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Asset Environment
                  <Info size={16} style={{ marginLeft: '0.5rem', color: '#6b7280' }} />
                </Label>
                <Input
                  type="text"
                  placeholder="Production, Development, Testing, Staging"
                  disabled
                  style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </FormGroup>

              <FormGroup mb="0">
                <Label>
                  Security Zone
                  <Info size={16} style={{ marginLeft: '0.5rem', color: '#6b7280' }} />
                </Label>
                <Input
                  type="text"
                  placeholder="DMZ, Internal, External, Restricted"
                  disabled
                  style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </FormGroup>
            </FormSection>
          </FormContainer>

          <ActionBar>
            <Button secondary onClick={() => navigate('/add-assets')}>
              Cancel Changes
            </Button>
            <Button
              primary
              onClick={handleUpdateAsset}
              disabled={!validation.step1Valid}
            >
              <Save size={20} />
              Update Network Asset
            </Button>
          </ActionBar>
        </MainContent>
      </Container>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'white',
            color: '#1e293b',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            fontWeight: '500',
          },
        }}
      />
    </PageWrapper>
  );
}

export default EditAssetsNetwork;
